import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';

type ApiEnvelope<T> = Partial<ApiResponse<T>> & {
  Data?: T;
  Message?: string;
};

export type SubmitEmployeeReviewRequest = { reviewId: number; preferSpaLinks: true };
export type ApproveEmployeeReviewRequest = { reviewId: number; preferSpaLinks: true };
export type SetEmployeeReviewFeedbackDoneRequest = { reviewId: number; isFeedbackDone: boolean };
export type EmployeeReviewFeedbackDoneResult = { message: string; status: string };
export type SubmitParticipantFeedbackRequest = {
  targetId?: string;
  hrmEmployeeReviewId: number;
  ratings: { teamwork: number; communication: number; helpfulness: number; reliability: number };
  professionalLevel: number;
  feedback: string;
};
export type ExternalFeedbackLinkRequest = { reviewId: number; participantId: number };
export type ExternalFeedbackLinkResult = { feedbackUrl: string };
type ExternalFeedbackLinkResponse = { FeedbackUrl: string };
export type EmployeeReviewPeriod = { periodFrom?: string; periodTo?: string };
export type EmployeeReviewCommandAvailability = {
  canSubmit: boolean;
  canApprove: boolean;
  canSetFeedbackDone: boolean;
  isFeedbackDone: boolean;
};
type EmployeeReviewCommandAvailabilityResponse = EmployeeReviewCommandAvailability & {
  CanSubmit?: boolean;
  CanApprove?: boolean;
  CanSetFeedbackDone?: boolean;
  IsFeedbackDone?: boolean;
};

const getResponseData = <T>(response: ApiEnvelope<T>) => response.data ?? response.Data;

const getResponseCode = (response: ApiEnvelope<unknown>) => {
  const data = getResponseData(response) as { Code?: number; code?: number } | undefined;
  return response.Code ?? response.code ?? data?.Code ?? data?.code;
};

const getMessage = (response: ApiEnvelope<unknown>, fallback: string) => {
  const code = getResponseCode(response);
  if (code === 1) {
    const data = getResponseData(response);
    const nested = data as { Message?: string; message?: string } | undefined;
    return typeof data === 'string' && data
      ? data
      : (nested?.Message ?? nested?.message ?? response.message ?? response.Message ?? fallback);
  }
  throw new Error(extractErrorMessage(response, fallback));
};

export const employeeReviewApi = createApi({
  reducerPath: 'employeeReviewApi',
  baseQuery,
  tagTypes: ['EmployeeReviewCommandAvailability'],
  endpoints: (builder) => ({
    getDefaultEmployeeReviewPeriod: builder.query<EmployeeReviewPeriod, void>({
      query: () => ({ url: '/umbraco/api/EmployeeReview/GetDefaultPeriod' }),
      transformResponse: (
        response:
          | ApiEnvelope<EmployeeReviewPeriod>
          | { PeriodFrom?: string; PeriodTo?: string; periodFrom?: string; periodTo?: string }
      ) => {
        const envelope = response as ApiEnvelope<EmployeeReviewPeriod>;
        const data = (getResponseData(envelope) ?? response) as {
          PeriodFrom?: string;
          PeriodTo?: string;
          periodFrom?: string;
          periodTo?: string;
        };
        const periodFrom = data?.periodFrom ?? data?.PeriodFrom;
        const periodTo = data?.periodTo ?? data?.PeriodTo;
        if (!periodFrom && !periodTo) {
          throw new Error(
            extractErrorMessage(response, 'Failed to resolve the employee review period.')
          );
        }
        return { periodFrom, periodTo };
      },
    }),
    getEmployeeReviewCommandAvailability: builder.query<EmployeeReviewCommandAvailability, number>({
      query: (reviewId) => ({
        url: '/umbraco/api/EmployeeReview/GetCommandAvailability',
        params: { reviewId },
      }),
      transformResponse: (response: ApiEnvelope<EmployeeReviewCommandAvailabilityResponse>) => {
        const availability = (getResponseData(response) ??
          response) as EmployeeReviewCommandAvailabilityResponse;
        if (!availability) {
          throw new Error(
            extractErrorMessage(response, 'Failed to load review action availability.')
          );
        }

        return {
          canSubmit: Boolean(availability.canSubmit ?? availability.CanSubmit),
          canApprove: Boolean(availability.canApprove ?? availability.CanApprove),
          canSetFeedbackDone: Boolean(
            availability.canSetFeedbackDone ?? availability.CanSetFeedbackDone
          ),
          isFeedbackDone: Boolean(availability.isFeedbackDone ?? availability.IsFeedbackDone),
        };
      },
      providesTags: (_result, _error, reviewId) => [
        { type: 'EmployeeReviewCommandAvailability', id: reviewId },
      ],
    }),
    submitEmployeeReview: builder.mutation<string, SubmitEmployeeReviewRequest>({
      query: (body) => ({ url: '/umbraco/api/EmployeeReview/Submit', method: 'POST', body }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        getMessage(response, 'Review submitted.'),
      invalidatesTags: (_result, _error, request) => [
        { type: 'EmployeeReviewCommandAvailability', id: request.reviewId },
      ],
    }),
    approveEmployeeReview: builder.mutation<string, ApproveEmployeeReviewRequest>({
      query: (body) => ({
        url: '/umbraco/api/EmployeeReview/ApproveByManager',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        getMessage(response, 'Review approved.'),
      invalidatesTags: (_result, _error, request) => [
        { type: 'EmployeeReviewCommandAvailability', id: request.reviewId },
      ],
    }),
    setEmployeeReviewFeedbackDone: builder.mutation<
      EmployeeReviewFeedbackDoneResult,
      SetEmployeeReviewFeedbackDoneRequest
    >({
      query: (body) => ({
        url: '/umbraco/api/EmployeeReview/SetFeedbackDone',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ Status?: string; status?: string }>) => {
        const message = getMessage(response, 'Feedback completion updated.');
        const data = (getResponseData(response) ?? response) as {
          Status?: string;
          status?: string;
        };
        const status = data?.status ?? data?.Status;
        if (!status) throw new Error('Feedback completion response did not include a status.');
        return { message, status };
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'EmployeeReviewCommandAvailability', id: request.reviewId },
      ],
    }),
    submitParticipantFeedback: builder.mutation<string, SubmitParticipantFeedbackRequest>({
      query: (body) => ({ url: '/umbraco/api/ColleagueFeedback/Post', method: 'POST', body }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        getMessage(response, 'Feedback submitted.'),
    }),
    getOrCreateExternalFeedbackLink: builder.mutation<
      ExternalFeedbackLinkResult,
      ExternalFeedbackLinkRequest
    >({
      query: (body) => ({
        url: '/umbraco/api/EmployeeReview/GetOrCreateExternalFeedbackLink',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ExternalFeedbackLinkResponse) => ({
        feedbackUrl: response.FeedbackUrl,
      }),
    }),
  }),
});

export const {
  useGetDefaultEmployeeReviewPeriodQuery,
  useLazyGetDefaultEmployeeReviewPeriodQuery,
  useGetEmployeeReviewCommandAvailabilityQuery,
  useSetEmployeeReviewFeedbackDoneMutation,
  useGetOrCreateExternalFeedbackLinkMutation,
} = employeeReviewApi;
